export interface PlaybackTiming {expiresIn:number;heartbeatIn:number}
interface RenewalClock {
  now():number;
  after(milliseconds:number,callback:()=>void):()=>void;
}
const browserClock:RenewalClock={now:()=>performance.now(),after:(delay,callback)=>{const timer=setTimeout(callback,delay);return()=>clearTimeout(timer);}};
const expiredMessage='播放授权已过期，请选择在此继续播放。';
const connectionMessage='播放连接中断，请重新开始播放。';

function retryable(error:unknown):boolean {
  if(error instanceof Error&&'status' in error&&typeof error.status==='number')return error.status===408||error.status===429||error.status>=500&&error.status<=599;
  return error instanceof TypeError||error instanceof Error&&error.name==='AbortError';
}

/** Retry only renewal of the existing lease. A transport failure does not revoke
 * a confirmed lease, and neither a retry nor a late reply extends its deadline.
 * Request-start timestamps make the deadline conservative by including latency. */
export function startPlaybackRenewal(options:{
  initial:PlaybackTiming;
  requestedAt:number;
  renew(signal:AbortSignal):Promise<PlaybackTiming>;
  onLost(message:string):void;
  clock?:RenewalClock;
}):()=>void {
  const clock=options.clock??browserClock;
  let stopped=false,deadline=0,cancelNext:(()=>void)|undefined,cancelExpiry:(()=>void)|undefined,cancelRequestTimeout:(()=>void)|undefined,controller:AbortController|undefined;
  const stop=()=>{if(stopped)return;stopped=true;cancelNext?.();cancelExpiry?.();cancelRequestTimeout?.();controller?.abort();};
  const fail=(message:string)=>{if(stopped)return;stop();options.onLost(message);};
  const schedule=(delay:number)=>{cancelNext?.();cancelNext=clock.after(Math.max(0,Math.min(delay,deadline-clock.now())),()=>void renew());};
  const accept=(timing:PlaybackTiming,sentAt:number)=>{
    if(!Number.isFinite(timing?.expiresIn)||timing.expiresIn<=0||timing.expiresIn>300||!Number.isFinite(timing.heartbeatIn)||timing.heartbeatIn<=0){fail('播放授权响应无效，请重新开始播放。');return;}
    deadline=sentAt+timing.expiresIn*1000;
    if(deadline<=clock.now()){fail(expiredMessage);return;}
    cancelExpiry?.();cancelExpiry=clock.after(deadline-clock.now(),()=>fail(expiredMessage));
    schedule(Math.max(5,Math.min(20,timing.heartbeatIn))*1000);
  };
  const renew=async()=>{
    if(stopped)return;
    if(clock.now()>=deadline){fail(expiredMessage);return;}
    const sentAt=clock.now(),request=new AbortController();controller=request;
    const cancelTimeout=clock.after(Math.min(10_000,deadline-sentAt),()=>request.abort());cancelRequestTimeout=cancelTimeout;
    try{
      const timing=await options.renew(request.signal);
      if(stopped)return;
      // A blocked tab may receive a late reply before its expiry timer runs.
      if(clock.now()>=deadline){fail(expiredMessage);return;}
      accept(timing,sentAt);
    }catch(error){
      if(stopped)return;
      if(clock.now()>=deadline){fail(expiredMessage);return;}
      if(retryable(error)){schedule(3000);return;}
      fail(error instanceof Error?error.message:connectionMessage);
    }finally{cancelTimeout();if(controller===request){controller=undefined;cancelRequestTimeout=undefined;}}
  };
  accept(options.initial,options.requestedAt);
  return stop;
}
